import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetSessionsQuery,
  useGetSessionByIdQuery,
  useUpdateSessionMutation,
  UpdateSessionPayload,
} from '@/store/api/sessionsApi';
import { Loader } from '@/components/common/Loader';
import { SessionForm } from './SessionForm';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';
import { ErrorBanner } from '@/components/common/ErrorBanner';

export function EditSessionPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);

  // Fetch all sessions (up to 1000) and find the one with matching ID
  // to get its slug
  const {
    data: listResponse,
    isLoading: isListLoading,
    error: listError,
  } = useGetSessionsQuery({ take: 1000 });
  const listSession = listResponse?.data.find((s) => s.id === sessionId);

  // Fetch the full detailed session (including contentBlocks) by slug
  const {
    data: session,
    isLoading: isSessionLoading,
    error: sessionError,
  } = useGetSessionByIdQuery(listSession?.slug || '', { skip: !listSession?.slug });

  const [updateSession, { isLoading: isUpdating }] = useUpdateSessionMutation();

  const { success: notifySuccess, error: notifyError } = useNotifications();

  const handleSubmit = async (data: UpdateSessionPayload) => {
    try {
      await updateSession({ id: sessionId, body: data }).unwrap();
      notifySuccess('Session saved successfully!');
    } catch (err: any) {
      const message = err?.data?.message || 'Failed to update session. Please try again.';
      notifyError(Array.isArray(message) ? message.join('\n') : message);
    }
  };

  const handleAutoSave = async (data: UpdateSessionPayload) => {
    // Silently update without navigating
    await updateSession({ id: sessionId, body: data }).unwrap();
  };

  if (isListLoading || isSessionLoading) {
    return <Loader />;
  }

  if (listError || sessionError || !session) {
    return (
      <ErrorBanner
        type="error"
        title="Session not found"
        message="The session you are trying to edit does not exist."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sessions')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Edit Session</h1>
          <p className="text-muted-foreground truncate max-w-md">{session.title}</p>
        </div>
      </div>

      <SessionForm
        initialData={session}
        onSubmit={handleSubmit}
        onAutoSave={handleAutoSave}
        isSubmitting={isUpdating}
        submitLabel="Save Changes"
      />
    </div>
  );
}
